import React, { useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

import Navbar from './Navbar';
import HeroSection from './HeroSection';
import SelectedWorksSection from './SelectedWorksSection';
import HomeAboutTeaser from './HomeAboutTeaser';
import ServicesSection from './ServicesSection';
import ProcessSection from './ProcessSection';
import TestimonialsSection from './TestimonialsSection';
import ContactSection from './ContactSection';
import SEO from './SEO';
import { useLocale } from '../i18n/LocaleContext';

const buildHomepageJsonLd = (homeUrl, description) => ({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': 'https://skdesign.be/#website',
      url: homeUrl,
      name: 'SK Design',
      alternateName: 'Sander Kuyken — Creative Designer',
      publisher: { '@id': 'https://skdesign.be/#person' },
    },
    {
      '@type': 'Person',
      '@id': 'https://skdesign.be/#person',
      name: 'Sander Kuyken',
      alternateName: 'SK Design',
      jobTitle: 'Freelance Creative Designer',
      url: homeUrl,
      email: 'mailto:info@skdesign.be',
      sameAs: [
        'https://www.linkedin.com/in/sander-kuyken-8a384716b/',
        'https://www.artstation.com/kuyken',
      ],
      knowsAbout: ['Branding', 'Web Design', 'Motion Design', '3D', 'VFX', 'Logo Design'],
      description,
    },
    {
      '@type': 'ProfessionalService',
      '@id': 'https://skdesign.be/#business',
      name: 'SK Design',
      url: homeUrl,
      email: 'info@skdesign.be',
      founder: { '@id': 'https://skdesign.be/#person' },
      areaServed: 'Belgium',
      vatID: 'BE1011.925.180',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Op de brikkenoven 6',
        addressLocality: 'Bilzen-Hoeselt',
        postalCode: '3740',
        addressCountry: 'BE',
      },
      knowsAbout: ['Branding & identity design', 'Motion graphics', '3D & VFX', 'Web & UX design'],
    },
  ],
});

gsap.registerPlugin(ScrollTrigger);

const HomePage = () => {
  const { t, loc } = useLocale();
  const homeUrl = `https://skdesign.be${loc('/')}`;
  useEffect(() => {
    // Initialize scroll reveal animations
    const scrollElements = document.querySelectorAll('.scroll-reveal');

    scrollElements.forEach((element) => {
      gsap.fromTo(element,
        {
          opacity: 0,
          y: 50
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: element,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    });

    // Smooth scroll behavior
    const links = document.querySelectorAll('a[href^="#"]');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <main>
      <SEO
        title={t('home.metaTitle')}
        description={t('home.metaDescription')}
        path="/"
        jsonLd={buildHomepageJsonLd(homeUrl, t('home.metaDescription'))}
      />
      <Navbar />
      <section id="hero">
        <HeroSection />
      </section>

      <section id="works">
        <SelectedWorksSection />
      </section>

      {/* About teaser (Fase 2) */}
      <section id="about-teaser">
        <HomeAboutTeaser />
      </section>

      <section id="services">
        <ServicesSection />
      </section>

      {/* Process (Fase 2) */}
      <section id="process">
        <ProcessSection />
      </section>

      {/* Client proof (Fase 6) — now backed by realistic, project-based,
          attributable testimonials in data/testimonials.js. */}
      <section id="testimonials">
        <TestimonialsSection />
      </section>

      <section id="contact">
        <ContactSection />
      </section>
    </main>
  );
};

export default HomePage;


